import { useState, useRef, useEffect, useCallback } from "react";
import styles from "./ShopSwitcher.module.css";
import { useShop } from "../contexts/ShopContext";

export default function ShopSwitcher() {
  const { shops, selectedShop, setSelectedShopId, loadingShops } = useShop();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleSelect = useCallback(
    (id) => {
      setSelectedShopId(id);
      setOpen(false);
    },
    [setSelectedShopId],
  );

  // Don't render if only one shop or still loading with no shops
  if (loadingShops && shops.length === 0) return null;
  if (shops.length <= 1 && !loadingShops) return null;

  const displayName = selectedShop?.name ?? selectedShop?.domain ?? "Select shop";

  return (
    <div className={styles.wrapper} ref={ref}>
      <button
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={styles.shopIcon}>🏪</span>
        <span className={styles.shopName}>{displayName}</span>
        <span className={`${styles.chevron} ${open ? styles.chevronUp : ""}`}>▾</span>
      </button>

      {open && (
        <ul className={styles.dropdown} role="listbox">
          {shops.map((shop) => {
            const isSelected = shop.id === selectedShop?.id;
            return (
              <li
                key={shop.id}
                role="option"
                aria-selected={isSelected}
                className={`${styles.option} ${isSelected ? styles.optionActive : ""}`}
                onClick={() => handleSelect(shop.id)}
              >
                <span className={styles.optionName}>
                  {shop.name ?? shop.domain}
                </span>
                {shop.subscription?.planFamily === "MONTHLY_SUBSCRIPTION" ? (
                  <span className={styles.badge}>PRO</span>
                ) : (
                  <span className={`${styles.badge} ${styles.badgeFree}`}>FREE</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
